import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { ChevronLeft, ChevronRight, Phone, Mail, AlertTriangle } from "lucide-react";

const WD = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

export default function TimeOffCalendar() {
  const [entries, setEntries] = useState([]);
  const [offset, setOffset] = useState(0);
  const [dayOpen, setDayOpen] = useState(null);

  useEffect(() => {
    api.get("/admin/time-off-calendar").then(({ data }) => setEntries(data.entries || [])).catch(() => {});
  }, []);

  const base = new Date();
  const view = new Date(base.getFullYear(), base.getMonth() + offset, 1);
  const year = view.getFullYear();
  const month = view.getMonth();
  const monthName = view.toLocaleString(undefined, { month: "long", year: "numeric" });
  const firstDow = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();

  const byDate = {};
  entries.forEach((e) => { (byDate[e.date] = byDate[e.date] || []).push(e); });

  const cells = [];
  for (let i = 0; i < firstDow; i++) cells.push(null);
  for (let d = 1; d <= daysInMonth; d++) {
    const ds = `${year}-${String(month + 1).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
    cells.push({ d, ds, list: byDate[ds] || [] });
  }

  const distinctPros = (list) => new Set(list.map((x) => x.pro_id)).size;

  return (
    <div className="card-metal p-5" data-testid="timeoff-calendar">
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-black text-lg">{monthName}</h3>
        <div className="flex gap-2">
          <button onClick={() => setOffset(offset - 1)} data-testid="cal-prev" className="btn-silver-3d !py-1.5 !px-3"><ChevronLeft className="w-4 h-4 text-[#9b111e]" /></button>
          <button onClick={() => setOffset(0)} data-testid="cal-today" className="btn-silver-3d !py-1.5 !px-3"><span className="text-engraved text-xs">Today</span></button>
          <button onClick={() => setOffset(offset + 1)} data-testid="cal-next" className="btn-silver-3d !py-1.5 !px-3"><ChevronRight className="w-4 h-4 text-[#9b111e]" /></button>
        </div>
      </div>
      <div className="flex items-center gap-4 text-xs text-slate-500 mb-2">
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-amber-200 inline-block" /> 1 pro off</span>
        <span className="flex items-center gap-1"><span className="w-3 h-3 rounded bg-red-400 inline-block" /> overlap (2+ pros off)</span>
      </div>
      <div className="grid grid-cols-7 gap-1">
        {WD.map((w) => <div key={w} className="text-center text-xs font-bold text-slate-400 py-1">{w}</div>)}
        {cells.map((c, i) => {
          if (!c) return <div key={`e${i}`} />;
          const n = distinctPros(c.list);
          const bg = n >= 2 ? "bg-red-400 text-white" : n === 1 ? "bg-amber-200" : "bg-slate-50";
          return (
            <button key={c.ds} onClick={() => c.list.length && setDayOpen({ ds: c.ds, list: c.list })}
              data-testid={`cal-day-${c.ds}`}
              className={`min-h-[54px] rounded-lg p-1 text-left ${bg} ${c.list.length ? "cursor-pointer hover:ring-2 hover:ring-[#9b111e]" : "cursor-default"}`}>
              <div className="text-xs font-bold">{c.d}</div>
              {n >= 2 && <div className="flex items-center gap-0.5 text-[10px] font-bold"><AlertTriangle className="w-3 h-3" />{n} off</div>}
              {n === 1 && <div className="text-[10px] truncate">{c.list[0].pro_name}</div>}
            </button>
          );
        })}
      </div>

      <Dialog open={!!dayOpen} onOpenChange={(o) => !o && setDayOpen(null)}>
        <DialogContent className="bg-white">
          <DialogHeader>
            <DialogTitle className="font-heading">Pros off on {dayOpen?.ds}</DialogTitle>
            <DialogDescription>{dayOpen && distinctPros(dayOpen.list) >= 2 ? "⚠️ Overlap — multiple pros are unavailable this day." : "One-click contact below."}</DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {dayOpen?.list.map((e, idx) => (
              <div key={idx} className="flex flex-wrap items-center justify-between gap-2 border border-slate-200 rounded-lg p-3" data-testid={`day-pro-${e.pro_id}`}>
                <div>
                  <p className="font-semibold">{e.pro_name}
                    <span className={"ml-2 badge-tier " + (e.status === "approved" ? "bg-green-100 text-green-700" : "bg-amber-100 text-amber-700")}>{e.status}</span>
                    <span className="ml-1 text-xs text-slate-400 capitalize">{e.category}</span>
                  </p>
                  <p className="text-xs text-slate-500">{e.phone || "no phone"} · {e.email}</p>
                </div>
                <div className="flex gap-2">
                  {e.phone && <a href={`tel:${e.phone}`} className="btn-red-3d !py-1.5 !px-3" data-testid={`call-${e.pro_id}`}><Phone className="w-4 h-4" /></a>}
                  <a href={`mailto:${e.email}`} className="btn-silver-3d !py-1.5 !px-3" data-testid={`email-${e.pro_id}`}><Mail className="w-4 h-4 text-[#9b111e]" /></a>
                </div>
              </div>
            ))}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
